import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import Seo from "@/components/Seo";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [referred, setReferred] = useState(false);
  const [welcomeCredit, setWelcomeCredit] = useState(10);

  useEffect(() => {
    setReferred(!!localStorage.getItem("dg_ref"));
    api.get("/rewards/public").then(({ data }) => {
      if (data.welcome_credit != null) setWelcomeCredit(data.welcome_credit);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      toast.success("Account created. Welcome to Dark Growers.");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-4 py-3.5 transition-colors placeholder:text-slate-600";

  return (
    <div className="max-w-md mx-auto px-6 py-24">
      <Seo title="Create Account | Dark Growers Genetics" description="Create a Dark Growers Genetics account to shop premium clones and seeds, track orders, and check out faster." noindex />
      <p className="label-caps text-emerald-500 mb-3 text-center">Join the community</p>
      <h1 className="font-display text-5xl text-slate-100 text-center mb-10">Create Account</h1>
      {referred && (
        <div data-testid="referral-banner" className="flex items-center gap-3 border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 mb-6 text-sm text-emerald-300">
          <Gift size={18} className="shrink-0" />
          <span>You were referred! Get a <b>${welcomeCredit.toFixed(0)} store credit</b> toward your first order when you sign up. Store credit only — no cash value.</span>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <input required placeholder="Full name" data-testid="register-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input type="email" required placeholder="Email" data-testid="register-email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input type="password" required minLength={6} placeholder="Password (min 6 chars)" data-testid="register-password" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        {error && <p className="text-red-400 text-sm" data-testid="register-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 transition-colors font-medium py-3.5 text-sm tracking-wide">
          {loading ? "Creating..." : "Create Account"}
        </button>
      </form>
      <p className="text-slate-500 text-sm text-center mt-6">
        Already have an account?{" "}
        <Link to="/login" className="text-emerald-400 hover:text-emerald-300">Sign in</Link>
      </p>
    </div>
  );
}
